/**
 * Uni Forms — React pages.  Same shape as ``UniEmailPages.jsx`` /
 * ``UniSmsPages.jsx``; surfaces the form-builder side of the Uni
 * Forms module.
 *
 * Pages exported:
 *   * UniFormsDashboard — landing KPIs + quick links + quick create.
 *   * UniFormsForms     — form list + "New form" + "Duplicate" + "New link".
 *   * UniFormsForMe     — forms somebody else picked ME for.
 *   * UniFormsMySubmissions — forms I have actually filled, own rows only.
 *   * UniFormsQuizzes   — quiz list + "New quiz".
 *   * UniFormsPolls     — poll list + "New poll".
 *   * UniFormsTemplates — pre-built templates gallery + "Use template".
 *
 * Creating records
 * ----------------
 * Each list screen owns a small create modal that POSTs to the same
 * DRF collection it lists (``POST /api/uni-forms/forms/`` etc.).  The
 * viewsets are ``ModelViewSet``s and stamp ``created_by`` from the
 * request user (``_OwnerStampedMixin``), so the SPA only has to send
 * the handful of fields a human actually picks — everything else on
 * the model has a sensible default.  Newly created records always
 * start life as a *draft* so nothing is publicly reachable by
 * accident; publishing is an explicit status change.
 *
 * The public-fill experience (i.e. anonymous users actually filling
 * a form) is intentionally NOT moved into the SPA yet — the legacy
 * Django views in ``uni_forms/views.py`` still own that surface, and
 * the SPA only needs the *builder* views right now.  See
 * ``uni_forms/urls.py`` and ``uni_forms/api_urls.py`` for the
 * server-side rationale.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deleteJSON, getJSON, patchJSON, postJSON } from '../../api/client.js';
import { ROUTES } from '../../config.js';
import {
  fmtCount, fmtPct, UniActionButton, UniBool, UniCheckField, UniConfirmModal,
  UniCopyField, UniDetailRow, UniFormError, UniKpiCard, UniModal, UniPageShell,
  UniPickList, UniRowActions, UniSection, UniSelectField, UniSpecViewModal,
  UniStatusPill, UniTable, UniTextArea, UniTextField, UniTimeField, UniTile, UniToolbar,
  useUniAction, useUniCopy, useUniCreate, useUniDelete, useUniEdit, useUniList,
} from './UniShared.jsx';
import { formatInTimeZone, useOrgTimeZone } from '../../lib/displayTimezone.js';





const EP = {
  FORMS:     '/api/uni-forms/forms/',
  DELETED:   '/api/uni-forms/forms/deleted/',
  FOR_ME:    '/api/uni-forms/forms/for-me/',
  MY_SUBMISSIONS: '/api/uni-forms/forms/my-submissions/',
  USERS:     '/api/uni-forms/audience-users/',
  QUIZZES:   '/api/uni-forms/quizzes/',
  POLLS:     '/api/uni-forms/polls/',
  TEMPLATES: '/api/uni-forms/templates/',
};

const THEME = 'forms';

/* Choice lists mirrored from ``uni_forms/models.py``.  Kept inline
   rather than fetched: they're small, stable and part of the model's
   public contract (they also feed the DRF enum names in settings). */
const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft — only you can see it' },
  { value: 'published', label: 'Published — accepting responses' },
  { value: 'closed',    label: 'Closed — no new responses' },
];

const FORM_TYPE_OPTIONS = [
  { value: 'contact',      label: 'Contact / Generic' },
  { value: 'registration', label: 'User Registration' },
  { value: 'login',        label: 'User Login' },
  { value: 'calculation',  label: 'Calculation / Quote' },
  { value: 'survey',       label: 'Survey' },
];

const QUIZ_TYPE_OPTIONS = [
  { value: 'knowledge',   label: 'Knowledge quiz — scored answers' },
  { value: 'personality', label: 'Personality quiz — outcome based' },
];

const POLL_RESULT_OPTIONS = [
  { value: 'after_vote',  label: 'After the visitor votes' },
  { value: 'always',      label: 'Always (live results)' },
  { value: 'after_close', label: 'Only after the poll closes' },
  { value: 'never',       label: 'Never (private)' },
];


/* ── Who can open the form ────────────────────────────────────────────
   Mirrors ``Form.VISIBILITY_CHOICES`` for *fill* access.  Public and
   internal are the two options authors pick.  ``restricted`` is a
   legacy fill-gate still stored on some rows; it is only offered when
   that is already the current value so we don't silently widen access.

   "Display this form for selected internal users" is a *separate*
   checkbox (``show_to_selected_internal_users``).  It lists the form
   on those people's For Me tab and works with public *or* internal. */
export const VISIBILITY_OPTIONS = [
  { value: 'public',     label: 'Public — anyone with the link can fill it' },
  { value: 'internal',   label: 'Internal — any signed-in member of this organisation' },
];

export const REMINDER_PUSH_AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Everyone this form is shared with' },
  { value: 'not_submitted', label: 'Only people who have not submitted yet' },
];

export const REMINDER_PUSH_WEEKDAYS = [
  { value: 0, label: 'Sun', full: 'Sunday' },
  { value: 1, label: 'Mon', full: 'Monday' },
  { value: 2, label: 'Tue', full: 'Tuesday' },
  { value: 3, label: 'Wed', full: 'Wednesday' },
  { value: 4, label: 'Thu', full: 'Thursday' },
  { value: 5, label: 'Fri', full: 'Friday' },
  { value: 6, label: 'Sat', full: 'Saturday' },
];
export const REMINDER_PUSH_ALL_DAYS = REMINDER_PUSH_WEEKDAYS.map((d) => d.value);
export const DEFAULT_REMINDER_TIME = '09:00';

export function reminderTimeValue(raw) {
  const match = String(raw || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return DEFAULT_REMINDER_TIME;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    !Number.isInteger(hours)
    || !Number.isInteger(minutes)
    || hours > 23
    || minutes > 59
  ) {
    return DEFAULT_REMINDER_TIME;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function reminderDaysValue(raw) {
  if (!Array.isArray(raw)) return [...REMINDER_PUSH_ALL_DAYS];
  const next = [...new Set(raw.map((d) => Number(d)).filter((d) => d >= 0 && d <= 6))];
  next.sort((a, b) => a - b);
  return next;
}

export function reminderScheduleLabel({ enabled, time, days } = {}) {
  if (!enabled) return '';
  const clock = reminderTimeValue(time);
  const picked = Array.isArray(days)
    ? [...new Set(days.map((d) => Number(d)).filter((d) => d >= 0 && d <= 6))].sort((a, b) => a - b)
    : [...REMINDER_PUSH_ALL_DAYS];
  if (picked.length === 0) return `no days selected (${clock})`;
  if (picked.length === 7) return `every day at ${clock}`;
  return `${picked.map((d) => REMINDER_PUSH_WEEKDAYS[d].label).join(', ')} at ${clock}`;
}

const LEGACY_RESTRICTED_OPTION = {
  value: 'restricted',
  label: 'Restricted — only selected internal users may fill it',
};

export function visibilityOptionsFor(current) {
  if (current === 'restricted') {
    return [...VISIBILITY_OPTIONS, LEGACY_RESTRICTED_OPTION];
  }
  return VISIBILITY_OPTIONS;
}

/** True when the people picker should be on screen. */
export function needsUserPicker(draft) {
  if (!draft) return false;
  if (draft.show_to_selected_internal_users === true) return true;
  if (draft.show_to_selected_internal_users === false) return false;
  return draft.visibility === 'restricted';
}


/** The keys a new form is created with.  Shared by both create modals. */
const NEW_FORM_DRAFT = {
  title: '', description: '', form_type: 'contact', status: 'draft',
  collect_email: false,
  show_respondent_identity: true,
  allow_view_own_responses: false,
  allow_edit_response: false,
  allow_admin_edit_response: false,
  // The audience travels with the create POST — see ``AudienceFields``.
  // ``visibility`` defaults to the model's own default so the payload
  // never disagrees with the server about what "unset" means.
  visibility: 'public',
  show_to_selected_internal_users: false,
  audience_users: [], internal_notice: '',
  reminder_push_enabled: false,
  reminder_push_audience: 'not_submitted',
  reminder_push_message: '',
  reminder_push_time: DEFAULT_REMINDER_TIME,
  reminder_push_days: [...REMINDER_PUSH_ALL_DAYS],
};


/** The fields the Edit modal PATCHes.  ``useUniEdit`` sends only changes. */
const EDIT_FORM_FIELDS = [
  'title', 'description', 'form_type', 'status', 'collect_email',
  'show_respondent_identity', 'allow_view_own_responses',
  'allow_edit_response', 'allow_admin_edit_response',
  // ``audience_users`` is the M2M's write surface: DRF's
  // ``PrimaryKeyRelatedField(many=True)`` accepts a list of ids and
  // ``ModelSerializer.update`` calls ``.set()`` on it, so sending the
  // whole list is a replace — which is what the picker produces.
  'visibility', 'show_to_selected_internal_users',
  'audience_users', 'internal_notice',
  'reminder_push_enabled', 'reminder_push_audience', 'reminder_push_message',
  'reminder_push_time', 'reminder_push_days',
];


/** Footer shared by every create modal: Cancel + a busy-aware Save. */
function CreateFooter({ busy, onCancel, onSave, saveLabel = 'Create', disabled }) {
  return (
    <>
      <button type="button" className="ty-btn ty-btn--secondary" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
      <button type="button" className="ty-btn" onClick={onSave} disabled={busy || disabled}>
        {busy ? 'Creating…' : saveLabel}
      </button>
    </>
  );
}


/* ====================================================================
   Audience — "who is this form for?"
   ====================================================================
   Sits in the create AND edit modals rather than only on the separate
   Audience screen, because "who is this for?" is part of writing a form,
   not an afterthought — and a form left on the default ``public`` while
   the author believed it was internal is a data-exposure bug, not a
   cosmetic one.

   How the people picker is fed
   ---------------------------
   ``/audience-users/`` supports ``?search=`` server-side and caps at 200
   rows, so a tenant with thousands of staff can still be searched.  The
   hook below therefore debounces the query up to the server instead of
   filtering a first page of 200 in the browser — otherwise "Zoya" would
   be unfindable in a big org simply because she sorts past the cap.

   ``UniPickList`` has its own client-side box that filters what it was
   given.  That stays useful (it narrows the already-loaded page with no
   round trip), so both layers are live: type here to fetch, type there
   to narrow.  ``selectedDetails`` is merged into the options so that a
   person who is already picked never disappears from the list just
   because the current search doesn't match them — losing part of a
   selection on an unrelated keystroke is exactly the failure
   ``UniPickList`` was written to avoid.
   */

/** Searchable people source for the audience picker. */
function useAudienceUsers(enabled) {
  const [q, setQ] = React.useState('');
  // Debounced so a fast typist doesn't fire a request per keystroke; the
  // same 250ms ``UniToolbar`` uses, for a consistent feel.
  const [term, setTerm] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // ``null`` url short-circuits ``useUniList`` — no request until the
  // picker is actually on screen, so choosing "Public" costs nothing.
  const url = enabled
    ? (term ? `${EP.USERS}?search=${encodeURIComponent(term)}` : EP.USERS)
    : null;
  const { rows, loading, error } = useUniList(url, [enabled, term]);

  return { rows, loading, error, search: q, setSearch: setQ };
}


/** One option for ``UniPickList`` from an ``/audience-users/`` row. */
function userOption(u) {
  return {
    value: u.id,
    label: u.label || u.username,
    // Email disambiguates two people with the same name — the single
    // most common reason to pick the wrong person from a list.
    sub: u.email || u.username,
  };
}


/**
 * Fill-access select + independent "show on For Me" picker.
 *
 * ``draft.visibility``, ``draft.show_to_selected_internal_users`` and
 * ``draft.audience_users`` are owned by the caller's ``useUniCreate`` /
 * ``useUniEdit`` draft, so the whole audience travels in the same
 * POST/PATCH as the rest of the form.
 *
 * ``selectedDetails`` is what the server already knows about the picked
 * people (``audience_user_details`` on the row).  On the edit modal that
 * is how names show up before any search has run.
 */
function AudienceFields({ draft, set, selectedDetails = [], lockNote }) {
  const forMe = needsUserPicker(draft);
  const users = useAudienceUsers(forMe);

  const options = React.useMemo(() => {
    const byId = new Map();
    selectedDetails.forEach((u) => byId.set(String(u.id), userOption(u)));
    users.rows.forEach((u) => byId.set(String(u.id), userOption(u)));
    return [...byId.values()];
  }, [users.rows, selectedDetails]);

  const picked = draft.audience_users || [];
  const fillHint = draft.visibility === 'internal'
    ? 'Sign-in is forced — anonymous visitors with the link are turned away.'
    : draft.visibility === 'restricted'
      ? 'Only the people you pick below may open and fill it. Listing it on For Me is the checkbox underneath.'
      : 'Anyone with the link, signed in or not — use for contact and lead forms.';

  return (
    <>
      <UniSelectField
        label="Who can open and fill this form"
        value={draft.visibility || 'public'}
        onChange={(v) => set('visibility', v)}
        options={visibilityOptionsFor(draft.visibility)}
        hint={fillHint}
      />

      <UniCheckField
        label="Display this form for selected internal users"
        checked={forMe}
        onChange={(v) => set('show_to_selected_internal_users', v)}
        hint="They will find it under “For Me”. Works with public and internal forms."
      />

      {lockNote}

      {forMe && (
        <>
          <UniFormError error={users.error} />
          <UniPickList
            label="Internal users this form is for"
            options={options}
            selected={picked}
            onChange={(v) => set('audience_users', v)}
            placeholder="Search people by name or email…"
            emptyText={
              users.loading
                ? 'Searching…'
                : 'No users found in this organisation.'
            }
            hint="They'll find this form under “For Me”. Add or remove people at any time."
          />
          {picked.length === 0 && (
            <div className="uni-note uni-note--warn">
              <span aria-hidden="true">⚠️</span>
              <span>
                Nobody is selected yet, so this form will not appear on
                anyone’s “For Me” tab. Pick at least one person above.
              </span>
            </div>
          )}
        </>
      )}

      {(draft.visibility === 'internal' || draft.visibility === 'restricted') && (
        <UniTextField
          label="Internal notice (optional)"
          value={draft.internal_notice || ''}
          onChange={(v) => set('internal_notice', v)}
          placeholder="e.g. For internal reporting only — do not share"
          hint="Shown as a banner at the top of the form."
        />
      )}
    </>
  );
}


function reminderAudienceLabel(value) {
  return REMINDER_PUSH_AUDIENCE_OPTIONS.find((o) => o.value === value)?.label
    || 'Only people who have not submitted yet';
}


/**
 * Push reminder: who on For Me gets a notification, and whether a
 * scheduled cron should send it as well as the "Send reminder" button.
 * Time + weekday ticks cover both "every day at 09:00" and "only Mon/Wed".
 */
function ReminderFields({ draft, set }) {
  const forMe = needsUserPicker(draft);
  const scheduled = !!draft.reminder_push_enabled;
  const days = reminderDaysValue(draft.reminder_push_days);
  const clock = reminderTimeValue(draft.reminder_push_time);

  const setEnabled = (on) => {
    set('reminder_push_enabled', on);
    if (on) {
      if (!draft.reminder_push_time) set('reminder_push_time', DEFAULT_REMINDER_TIME);
      const current = Array.isArray(draft.reminder_push_days) ? draft.reminder_push_days : [];
      if (current.length === 0) set('reminder_push_days', [...REMINDER_PUSH_ALL_DAYS]);
    }
  };

  const toggleDay = (day) => {
    const current = reminderDaysValue(draft.reminder_push_days);
    const has = current.includes(day);
    const next = has ? current.filter((d) => d !== day) : [...current, day];
    next.sort((a, b) => a - b);
    set('reminder_push_days', next);
  };

  return (
    <>
      <UniSelectField
        label="Push reminder — who to notify"
        value={draft.reminder_push_audience || 'not_submitted'}
        onChange={(v) => set('reminder_push_audience', v)}
        options={REMINDER_PUSH_AUDIENCE_OPTIONS}
        hint="Sends a mobile push to people this form is displayed for (For Me)."
      />
      <UniCheckField
        label="Also send this reminder automatically every day"
        checked={scheduled}
        onChange={setEnabled}
        hint="Off = only when you click Send reminder. On = a daily push at the time you set below."
      />
      {scheduled && (
        <div className="uni-reminder-schedule">
          <UniTimeField
            label="Notification time"
            value={clock}
            onChange={(v) => set('reminder_push_time', reminderTimeValue(v))}
            hint="Pick the time of day the push should go out."
          />
          <div className="ty-field">
            <label>Days of the week</label>
            <div className="uni-weekday-picks" role="group" aria-label="Days of the week">
              {REMINDER_PUSH_WEEKDAYS.map((d) => {
                const on = days.includes(d.value);
                return (
                  <label
                    key={d.value}
                    className={`uni-weekday-pick${on ? ' is-on' : ''}`}
                    title={d.full}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleDay(d.value)}
                    />
                    <span>{d.label}</span>
                  </label>
                );
              })}
            </div>
            <div className="uni-weekday-picks__shortcuts">
              <button
                type="button"
                className="uni-row-btn"
                onClick={() => set('reminder_push_days', [...REMINDER_PUSH_ALL_DAYS])}
              >
                Every day
              </button>
              <button
                type="button"
                className="uni-row-btn"
                onClick={() => set('reminder_push_days', [1, 2, 3, 4, 5])}
              >
                Weekdays
              </button>
            </div>
            <span className="uni-field__hint">
              Tick every day for a daily reminder, or pick specific days (Sunday, Monday, …).
            </span>
            {days.length === 0 && (
              <div className="uni-note uni-note--warn" style={{ marginTop: 8 }}>
                <span aria-hidden="true">⚠️</span>
                <span>
                  No days selected — the schedule will not send. Tick at least one day,
                  or use Send reminder when you want to nudge people now.
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <UniTextField
        label="Reminder message (optional)"
        value={draft.reminder_push_message || ''}
        onChange={(v) => set('reminder_push_message', v)}
        placeholder="Please fill in this form"
        hint="Shown on the lock screen. Leave blank for a default “please fill this form”."
      />
      {!forMe && (
        <div className="uni-note uni-note--info">
          <span aria-hidden="true">ℹ️</span>
          <span>
            Turn on “Display this form for selected internal users” and pick
            people above — reminders go to that list.
          </span>
        </div>
      )}
    </>
  );
}


/* ====================================================================
   The shareable link
   ====================================================================
   A form nobody can reach collects nothing, so the fill link is the
   single most-copied string on this screen.  Three details decide
   whether "copy the link" actually works:

   1. WHICH link.  ``FormSerializer`` publishes both ``public_url``
      (absolute, built from the request host) and ``share_url``
      (relative, straight off the model).  Prefer the absolute one and
      only promote the relative path when it is missing — an older
      backend, or a serializer used without a request in context.
   2. Promoting it correctly.  ``window.location.origin`` is the right
      base precisely because Taskydo is multi-tenant: the SPA is served
      from the tenant's own subdomain, so the origin in the address bar
      is the host the respondent must land on.  A hard-coded domain
      would send every tenant's respondents to the wrong schema.
   3. WHETHER to offer it.  A draft form's link answers "Form closed",
      so handing someone a copy button for it invites them to share a
      dead URL.  ``canShare`` gates that.
   */

/** Absolute, pasteable fill URL for a form row — '' when unavailable. */
export function formPublicUrl(row) {
  if (!row) return '';
  if (row.public_url) return String(row.public_url);
  const rel = row.share_url;
  if (!rel) return '';
  // Already absolute (a backend that changed its mind) — pass through.
  if (/^[a-z][a-z0-9+.-]*:/i.test(rel) || rel.startsWith('//')) return String(rel);
  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
  return origin ? `${origin}${rel.startsWith('/') ? '' : '/'}${rel}` : String(rel);
}

/** Public fill URL opened in "amend my last submission" mode. */
export function formPublicEditUrl(row) {
  const url = formPublicUrl(row);
  if (!url) return '';
  const join = url.includes('?') ? '&' : '?';
  return `${url}${join}edit=1&start=1`;
}

/** Nested responses screen, forced to the caller's own rows. */
export function formMyResponsesPath(id) {
  return `${ROUTES.UNI_FORMS_FORM_RESPONSES(id)}?self=1`;
}


/**
 * Whether this form's link is worth sharing yet.
 *
 * ``status`` rather than ``is_accepting``: a published form that has hit
 * its response cap or its end date still has a *correct* link
 * worth keeping — it just isn't taking answers today.  A draft has never
 * had a working link at all.  The slug is stable until the owner
 * explicitly refreshes it (``POST .../refresh-link/``).
 */
export function canShareForm(row) {
  return !!formPublicUrl(row) && row?.status !== 'draft' && !row?.is_deleted;
}


/**
 * May this caller mint a new fill URL for ``row``?
 *
 * Same predicate as the endpoint (``can_manage``), advertised on the
 * row as ``can_edit``.  A For Me recipient or view-only collaborator
 * must not see a button that would 403 — and must not be able to take
 * a live form offline for everyone who already has the current link.
 */
export function canRefreshFormLink(row) {
  return !!formPublicUrl(row) && row?.can_edit !== false;
}


/** May this caller push a fill reminder for ``row``? */
export function canSendFormReminder(row) {
  return row?.can_send_reminder !== false && row?.status !== 'draft' && !row?.is_deleted;
}


/* ====================================================================
   Duplicating a form
   ====================================================================
   Two things people do with a finished form: send it to somebody, and
   make the next version of it.  The second one used to mean rebuilding
   it by hand — the endpoint existed
   (``POST /api/uni-forms/forms/<id>/duplicate/``) but nothing in the SPA
   called it, so a tenant running the same survey each quarter retyped
   every question.

   Why the copy is a DRAFT and the user is not navigated away
   ---------------------------------------------------------
   The server forces ``status='draft'`` on a copy and never carries the
   source's responses, so the safe next step is "review it, then publish".
   That's a decision the user makes on this list, which is where they
   already are — so the interaction ends with the new row visible and a
   banner naming it, rather than a redirect that loses their search term
   and scroll position.  The banner links into the question builder for
   the common follow-up ("copy it, then change question 3").

   ``busyId`` is per-row: duplicating a 60-question form is not instant,
   and a page-wide flag would put "…" on every row's button while one of
   them worked.
   */

/** "Click Duplicate → POST → show the copy" state for a form list. */
function useDuplicateForm(reload) {
  const act = useUniAction();
  const [busyId, setBusyId] = React.useState(null);
  const [created, setCreated] = React.useState(null);

  const run = React.useCallback((row) => {
    setBusyId(row.id);
    setCreated(null);
    // Empty body on purpose — everything the copy needs is server-side.
    // ``{}`` rather than nothing so axios still sends a JSON content type.
    return act.run(() => postJSON(`${EP.FORMS}${row.id}/duplicate/`, {}))
      .then((clone) => {
        setCreated(clone);
        // The new row belongs at the top of the list (it is the most
        // recently updated), so the list has to be re-read — otherwise the
        // copy is invisible until the next navigation and the click looks
        // like it did nothing.
        reload();
        return clone;
      })
      // ``act.run`` re-throws so callers can chain; the error is already
      // captured in ``act.error`` and rendered, so swallow it here rather
      // than surfacing an unhandled rejection.
      .catch(() => null)
      .finally(() => setBusyId(null));
  }, [act, reload]);

  return {
    run, busyId, created,
    error: act.error,
    dismiss: () => setCreated(null),
  };
}


/** Success banner for a just-duplicated form — nothing when idle. */
function DuplicatedFormNote({ state }) {
  const row = state.created;
  if (!row) return null;
  return (
    <div className="uni-state" style={{ marginBottom: 12 }}>
      <div className="uni-state__icon">✅</div>
      <div className="uni-state__title">
        Copied to “{row.title}” — saved as a draft.
      </div>
      {/* Says what the copy did NOT take, because that is the whole reason
          duplicating is safe on a live form and the reason the new row
          reads "0 responses" next to an original that has thousands. */}
      <div className="uni-state__hint">
        Its questions came across; the original's responses did not. Publish
        the copy when you're ready to collect on it.
      </div>
      <div className="uni-state__hint">
        <UniActionButton to={ROUTES.UNI_FORMS_FORM_QUESTIONS(row.id)}>
          Edit its questions →
        </UniActionButton>
        {' '}
        <UniActionButton onClick={state.dismiss}>Dismiss</UniActionButton>
      </div>
    </div>
  );
}


/* ====================================================================
   Refreshing a form's public link
   ====================================================================
   The fill URL is keyed on ``slug``.  Until now that slug was assigned
   once on first save and never changed, so a leaked or stale link could
   only be retired by deleting the form.  ``POST .../refresh-link/``
   mints a new slug on the same row; the previous URL 404s and responses
   stay put.

   Confirmation is load-bearing: unlike Duplicate (which creates a new
   draft and leaves the original live), this takes the *current* link
   offline for everyone who already has it.  ``UniConfirmModal`` is the
   same dialog the Delete action uses, so the irreversibility is obvious.
   */

/** "Click New link → confirm → POST → update the row" state for a form. */
function useRefreshFormLink(onUpdated) {
  const [row, setRow] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const ask = React.useCallback((record) => {
    setRow(record);
    setError(null);
  }, []);

  const cancel = React.useCallback(() => {
    if (busy) return;
    setRow(null);
    setError(null);
  }, [busy]);

  const confirm = React.useCallback(async () => {
    if (!row) return null;
    setBusy(true);
    setError(null);
    try {
      const updated = await postJSON(`${EP.FORMS}${row.id}/refresh-link/`, {});
      setRow(null);
      onUpdated?.(updated);
      return updated;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setBusy(false);
    }
  }, [row, onUpdated]);

  return { open: row !== null, row, busy, error, ask, cancel, confirm };
}


/* ====================================================================
   Push reminder to people the form is shared with
   ====================================================================
   ``POST .../send-reminder/`` fans a mobile notification to the For Me
   audience.  The form's ``reminder_push_audience`` (all vs not
   submitted) travels with the request so an unsaved edit cannot
   surprise the owner — they save first, then send.  Confirmation is
   load-bearing because a push cannot be un-sent.
   */

function useSendFormReminder(onSent) {
  const [row, setRow] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [result, setResult] = React.useState(null);

  const ask = React.useCallback((record) => {
    setRow(record);
    setError(null);
  }, []);

  const cancel = React.useCallback(() => {
    if (busy) return;
    setRow(null);
    setError(null);
  }, [busy]);

  const confirm = React.useCallback(async () => {
    if (!row) return null;
    setBusy(true);
    setError(null);
    try {
      const payload = await postJSON(`${EP.FORMS}${row.id}/send-reminder/`, {
        audience: row.reminder_push_audience || 'not_submitted',
      });
      setRow(null);
      setResult({ form: row, ...payload });
      onSent?.(payload, row);
      return payload;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setBusy(false);
    }
  }, [row, onSent]);

  const dismiss = React.useCallback(() => setResult(null), []);

  return {
    open: row !== null, row, busy, error, ask, cancel, confirm,
    result, dismiss,
  };
}


function SentReminderNote({ state }) {
  const result = state.result;
  if (!result) return null;
  const n = result.recipient_count || 0;
  const queued = result.queued || 0;
  return (
    <div className="uni-state" style={{ marginBottom: 12 }}>
      <div className="uni-state__icon">📬</div>
      <div className="uni-state__title">
        Reminder sent to {fmtCount(n)} {n === 1 ? 'person' : 'people'}
        {result.form?.title ? ` for “${result.form.title}”` : ''}.
      </div>
      <div className="uni-state__hint">
        {queued
          ? `${fmtCount(queued)} device${queued === 1 ? '' : 's'} queued.`
          : 'No registered mobile devices yet — they will get it once they install the app.'}
        {result.skipped_submitted
          ? ` ${fmtCount(result.skipped_submitted)} already submitted and were skipped.`
          : ''}
      </div>
      <div className="uni-state__hint">
        <UniActionButton onClick={state.dismiss}>Dismiss</UniActionButton>
      </div>
    </div>
  );
}


/* ====================================================================
   Dashboard
   ==================================================================== */

export function UniFormsDashboard() {
  const forms     = useUniList(EP.FORMS     + '?page=1&page_size=1');
  const quizzes   = useUniList(EP.QUIZZES   + '?page=1&page_size=1');
  const polls     = useUniList(EP.POLLS     + '?page=1&page_size=1');
  const templates = useUniList(EP.TEMPLATES + '?page=1&page_size=1');
  // "Waiting on me" — the only count here that is about the viewer rather
  // than about the tenant, and the reason a non-author has any business
  // on this dashboard at all.
  const forMe     = useUniList(EP.FOR_ME    + '?page=1&page_size=1');
  const mySubs    = useUniList(EP.MY_SUBMISSIONS + '?page=1&page_size=1');

  const loading = forms.loading || quizzes.loading || polls.loading || templates.loading;
  const v = (raw) => (loading ? '…' : fmtCount(raw?.count));

  // Quick-create straight from the dashboard so a new tenant isn't
  // forced to hop to the list screen before building anything.  Same
  // draft shape as the Forms list so the audience picker works here too —
  // the two modals are literally the same component.
  const create = useUniCreate(
    EP.FORMS,
    { ...NEW_FORM_DRAFT },
    () => forms.reload(),
  );

  return (
    <UniPageShell
      theme={THEME}
      icon="🧩"
      title="Form Builder"
      subtitle="Forms, quizzes, polls, templates and submission analytics — for any audience."
      actions={
        <>
          <UniActionButton hero primary onClick={create.start}>+ New Form</UniActionButton>
          <UniActionButton hero to={ROUTES.UNI_FORMS_FORMS}>📝 Forms</UniActionButton>
          <UniActionButton hero to={ROUTES.UNI_FORMS_FOR_ME}>🙋 For Me</UniActionButton>
          <UniActionButton hero to={ROUTES.UNI_FORMS_MY_SUBMISSIONS}>📥 My Submissions</UniActionButton>
          <UniActionButton hero to="/uni-forms/quizzes">Quizzes</UniActionButton>
          <UniActionButton hero to="/uni-forms/polls">Polls</UniActionButton>
        </>
      }
    >
      <UniSection title="At a glance" />
      <div className="uni-kpis">
        {/* Each KPI links to the list it counts — a bare number invites
            "show me those", and the tiles below are one extra hop away. */}
        <UniKpiCard icon="📝" label="Forms"     value={v(forms.raw)}     sublabel="all-time"
                    to="/uni-forms/forms" />
        {/* Placed second, not last: for anyone who isn't a form author
            this is the only number on the page they can act on. */}
        <UniKpiCard icon="🙋" label="For Me"    value={v(forMe.raw)}
                    sublabel="shared with you"
                    to={ROUTES.UNI_FORMS_FOR_ME} />
        <UniKpiCard icon="📥" label="My Submissions" value={v(mySubs.raw)}
                    sublabel="forms you filled"
                    to={ROUTES.UNI_FORMS_MY_SUBMISSIONS} />
        <UniKpiCard icon="🧠" label="Quizzes"   value={v(quizzes.raw)}   sublabel="all-time"
                    to="/uni-forms/quizzes" />
        <UniKpiCard icon="📊" label="Polls"     value={v(polls.raw)}     sublabel="all-time"
                    to="/uni-forms/polls" />
        <UniKpiCard icon="🎨" label="Templates" value={v(templates.raw)} sublabel="gallery"
                    to="/uni-forms/templates" />

      </div>

      <UniSection title="Build" />
      <div className="uni-tiles">
        <UniTile to="/uni-forms/forms"     icon="📝" title="Forms"
                 sub="Custom forms, surveys and lead capture" />
        <UniTile to={ROUTES.UNI_FORMS_FOR_ME} icon="🙋" title="For Me"
                 sub="Forms a colleague selected you to fill" />
        <UniTile to={ROUTES.UNI_FORMS_MY_SUBMISSIONS} icon="📥" title="My Submissions"
                 sub="Every form you have filled — only your answers" />
        <UniTile to="/uni-forms/quizzes"   icon="🧠" title="Quizzes"
                 sub="Scored questionnaires with pass-marks" />
        <UniTile to="/uni-forms/polls"     icon="📊" title="Polls"
                 sub="Single-question polls with live results" />
        <UniTile to="/uni-forms/templates" icon="🎨" title="Templates"
                 sub="Start fast from a pre-built form" />
      </div>

      {/* Administer & analyse — the audience / figures / reports
          surfaces live in ``UniFormsAdminPages.jsx``.  Tiles here
          rather than only in the top-bar because these three are the
          screens an admin comes looking for, and the top-bar is
          already eight items long on this module. */}
      <UniSection title="Administer & analyse" />
      <div className="uni-tiles">
        <UniTile to="/uni-forms/audience" icon="🔐" title="Audience & visibility"
                 sub="Public or internal, plus an optional For Me list" />
        <UniTile to="/uni-forms/figures"  icon="🔗" title="Module figures"
                 sub="Pull numbers from Pharma SFA, tasks and more" />
        <UniTile to="/uni-forms/reports"  icon="📈" title="Reports"
                 sub="Per form type, per person, per date or per group" />
        <UniTile to={ROUTES.UNI_FORMS_REPORT_TEMPLATES} icon="📋" title="Report templates"
                 sub="Create, edit and run saved report views" />
      </div>

      <CreateFormModal create={create} />

    </UniPageShell>
  );
}


/* ====================================================================
   Forms
   ==================================================================== */

/** Create-a-form modal.  Shared by the dashboard and the Forms list. */
function CreateFormModal({ create }) {
  const { open, draft, busy, error, close, set, submit } = create;
  const canSave = draft.title.trim().length > 0;

  return (
    <UniModal
      open={open}
      title="New form"
      onClose={close}
      footer={
        <CreateFooter
          busy={busy}
          onCancel={close}
          onSave={() => submit({ ...draft, title: draft.title.trim() })}
          saveLabel="Create form"
          disabled={!canSave}
        />
      }
    >
      <UniFormError error={error} />
      <UniTextField
        label="Title"
        required
        autoFocus
        value={draft.title}
        onChange={(v) => set('title', v)}
        placeholder="e.g. Customer feedback 2026"
      />
      <UniTextArea
        label="Description"
        value={draft.description}
        onChange={(v) => set('description', v)}
        placeholder="Shown to respondents above the first question."
      />
      <div className="uni-form-row">
        <UniSelectField
          label="Form type"
          value={draft.form_type}
          onChange={(v) => set('form_type', v)}
          options={FORM_TYPE_OPTIONS}
        />
        <UniSelectField
          label="Status"
          value={draft.status}
          onChange={(v) => set('status', v)}
          options={STATUS_OPTIONS}
          hint="Drafts are private until you publish."
        />
      </div>
      <UniCheckField
        label="Collect respondent email"
        checked={!!draft.collect_email}
        onChange={(v) => set('collect_email', v)}
      />
      <UniCheckField
        label="Show respondent identity on the form"
        checked={draft.show_respondent_identity !== false}
        onChange={(v) => set('show_respondent_identity', v)}
        hint="Signed-in users see their profile; public visitors see “Anonymous”."
      />
      <UniCheckField
        label="Let respondents view their own submissions"
        checked={!!draft.allow_view_own_responses}
        onChange={(v) => set('allow_view_own_responses', v)}
        hint="When enabled, people who fill this form can open My Submissions (and For Me) to review only what they submitted — not everyone else's answers."
      />
      <UniCheckField
        label="Allow respondents to edit after submit"
        checked={!!draft.allow_edit_response}
        onChange={(v) => set('allow_edit_response', v)}
        hint="The person who submitted can change their answers later. There is no time limit — turn this off when amendments should stop."
      />
      <UniCheckField
        label="Allow form admins to edit submitted responses"
        checked={!!draft.allow_admin_edit_response}
        onChange={(v) => set('allow_admin_edit_response', v)}
        hint="You, organisation admins, and collaborators with edit access can change a respondent's answers. Edited rows are marked “Edited by admin” so they can be traced."
      />

      {/* The audience.  Declared here rather than left to the separate
          Audience screen so a form is never created as ``public`` by
          omission — see ``AudienceFields``.  ``requires_login`` is
          deliberately NOT offered alongside it: ``visibility`` supersedes
          it (``effective_requires_login`` ORs the two), and two switches
          that both mean "sign in first" is how a form ends up internal in
          one place and public in another. */}
      <AudienceFields draft={draft} set={set} />
      <ReminderFields draft={draft} set={set} />
    </UniModal>
  );
}


/**
 * Edit-a-form modal.
 *
 * Deliberately the same handful of fields as the create modal rather
 * than the whole model: this is the "rename it / publish it / close it"
 * dialog, which is what a list screen's Edit button is for.  Question
 * authoring is a different (and much larger) job and stays in the
 * builder.
 *
 * ``useUniEdit`` PATCHes only the fields that changed, so opening this
 * on a form whose theme colours or notification settings were tuned
 * elsewhere can't quietly reset them.
 *
 * The audience is editable here too, with one extra rule the create
 * modal doesn't need: an admin can *lock* a form's audience
 * (``visibility_locked``), and a locked form must be read-only for its
 * own owner — otherwise the lock is decoration.  ``can_manage_visibility``
 * on the row is the server's answer to "may this user change it?", and
 * the same predicate is re-checked in ``FormSerializer.validate()``, so a
 * client that ignores this gets a 400 rather than a silent write.
 */
function EditFormModal({ edit }) {
  const { open, draft, busy, error, close, set, submit, row } = edit;
  const canSave = String(draft.title || '').trim().length > 0;
  // ``!== false`` (not truthiness): an older backend that doesn't publish
  // the flag should leave the controls usable, and let the server decide.
  const canManageAudience = row?.can_manage_visibility !== false;

  return (
    <UniModal
      open={open}
      title="Edit form"
      onClose={close}
      footer={
        <>
          <button type="button" className="ty-btn ty-btn--secondary"
                  onClick={close} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="ty-btn"
                  onClick={() => submit({ ...draft, title: String(draft.title).trim() })}
                  disabled={busy || !canSave}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <UniFormError error={error} />
      <UniTextField
        label="Title"
        required
        autoFocus
        value={draft.title || ''}
        onChange={(v) => set('title', v)}
      />
      <UniTextArea
        label="Description"
        value={draft.description || ''}
        onChange={(v) => set('description', v)}
      />
      <div className="uni-form-row">
        <UniSelectField
          label="Form type"
          value={draft.form_type || 'contact'}
          onChange={(v) => set('form_type', v)}
          options={FORM_TYPE_OPTIONS}
        />
        <UniSelectField
          label="Status"
          value={draft.status || 'draft'}
          onChange={(v) => set('status', v)}
          options={STATUS_OPTIONS}
          hint="Publishing makes the form reachable by its audience."
        />
      </div>
      <UniCheckField
        label="Collect respondent email"
        checked={!!draft.collect_email}
        onChange={(v) => set('collect_email', v)}
      />
      <UniCheckField
        label="Show respondent identity on the form"
        checked={draft.show_respondent_identity !== false}
        onChange={(v) => set('show_respondent_identity', v)}
        hint="Signed-in users see their profile; public visitors see “Anonymous”."
      />
      <UniCheckField
        label="Let respondents view their own submissions"
        checked={!!draft.allow_view_own_responses}
        onChange={(v) => set('allow_view_own_responses', v)}
        hint="People who fill this form can open My Submissions to review only their own answers — not everyone's submissions."
      />
      <UniCheckField
        label="Allow respondents to edit after submit"
        checked={!!draft.allow_edit_response}
        onChange={(v) => set('allow_edit_response', v)}
        hint="The person who submitted can change their answers later. There is no time limit — turn this off when amendments should stop."
      />
      <UniCheckField
        label="Allow form admins to edit submitted responses"
        checked={!!draft.allow_admin_edit_response}
        onChange={(v) => set('allow_admin_edit_response', v)}
        hint="You, organisation admins, and collaborators with edit access can change a respondent's answers. Edited rows are marked “Edited by admin” so they can be traced."
      />

      {/* A locked audience is shown, not hidden: "why can't I change
          this?" is a better experience than a control that silently isn't
          there.  ``UniDetailRow`` rather than a disabled select, because a
          greyed-out dropdown still invites clicking. */}
      {canManageAudience ? (
        <AudienceFields
          draft={draft}
          set={set}
          selectedDetails={row?.audience_user_details || []}
        />
      ) : (
        <>
          <UniDetailRow label="Who can open and fill this form">
            {row?.visibility_display || row?.visibility}
          </UniDetailRow>
          {row?.show_to_selected_internal_users && (
            <UniDetailRow label="Displayed on For Me for">
              {(row.audience_user_details || [])
                .map((u) => u.label || u.username).join(', ') || 'Nobody selected'}
            </UniDetailRow>
          )}
          <div className="uni-note uni-note--lock">
            <span aria-hidden="true">🔒</span>
            <span>
              An administrator has locked this form's audience, so it can't be
              changed here. Ask an admin if it needs to move.
            </span>
          </div>
        </>
      )}
      <ReminderFields draft={draft} set={set} />
    </UniModal>
  );
}


/** Form admins see every submission; everyone else sees only their own. */
function seesEverySubmission(row) {
  return row?.counts_all_submissions === true;
}

function SubmissionCount({ row }) {
  const n = fmtCount(row?.response_count);
  if (seesEverySubmission(row)) {
    return <span className="uni-num" title="All submissions">{n}</span>;
  }
  return (
    <span className="uni-num" title="Only your submissions">
      {n}
      <span className="uni-num--muted"> yours</span>
    </span>
  );
}

/**
 * Read-only "View" drawer — the form's settings at a glance, and the
 * form's share link in full.
 *
 * The link block sits ABOVE the settings rows rather than at the bottom
 * with the other read-only values, because it is the reason most people
 * open this drawer at all: they want to send the form to someone.  Long
 * URLs are also the one value here that can't be usefully truncated —
 * a half-visible link is indistinguishable from a wrong one — so it gets
 * a full-width input instead of a ``UniDetailRow`` cell.
 *
 * ``onAskRefresh`` is optional: the For Me tab reuses this drawer so a
 * recipient can copy the fill link, but they must not be offered a
 * button that would mint a new URL on someone else's form.
 */
function ViewFormModal({ row, onClose, onAskRefresh, refreshBusy }) {
  const url = formPublicUrl(row);
  const shareable = canShareForm(row);
  const canRefresh = canRefreshFormLink(row) && typeof onAskRefresh === 'function';

  return (
    <UniModal
      open={!!row}
      title="Form details"
      onClose={onClose}
      width={600}
      footer={
        <button type="button" className="ty-btn ty-btn--secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      {row && (
        <>
          {row.is_deleted && (
            <div className="uni-note uni-note--warn" style={{ marginBottom: 12 }}>
              <span aria-hidden="true">💡</span>
              <span>
                This form is in <strong>Deleted</strong>. Its public link is
                off, and it is hidden from For Me, until you restore it.
                Questions and responses are still saved.
              </span>
            </div>
          )}
          {/* ---- The share link ------------------------------------- */}
          {url && shareable && !row.is_deleted && (
            <>
              <UniCopyField
                label="Public link — anyone with this can open the form"
                value={url}
                hint={
                  row.effective_requires_login || row.requires_login
                    ? 'Recipients are asked to sign in before they can answer.'
                    : 'No sign-in needed — paste it into an email, a chat or a QR code.'
                }
              />
              {canRefresh && (
                <FormLinkRefreshAction
                  onAsk={() => onAskRefresh(row)}
                  busy={refreshBusy}
                />
              )}
            </>
          )}
          {/* A draft's link resolves, but the page it opens says the form
              is closed.  Show the URL with a warning: publishing activates
              this exact URL, unless the owner refreshes it first. */}
          {url && !shareable && !row.is_deleted && (
            <>
              <div className="uni-note uni-note--warn">
                <span aria-hidden="true">💡</span>
                <span>
                  This form is a <strong>draft</strong>, so the link below
                  shows “form not available” to anyone who opens it. Set the
                  status to <strong>Published</strong> to make it live.
                </span>
              </div>
              <UniCopyField
                label="Public link (not live yet)"
                value={url}
                hint="Publishing activates this URL. Use New link if you need a different one first."
              />
              {canRefresh && (
                <FormLinkRefreshAction
                  onAsk={() => onAskRefresh(row)}
                  busy={refreshBusy}
                />
              )}
            </>
          )}

          <UniDetailRow label="Title">{row.title}</UniDetailRow>
          <UniDetailRow label="Description">{row.description}</UniDetailRow>
          <UniDetailRow label="Type">
            {row.form_type_display || row.form_type}
          </UniDetailRow>
          <UniDetailRow label="Status">
            <UniStatusPill value={row.status} />
          </UniDetailRow>
          <UniDetailRow label="Visibility">
            {row.visibility_display || row.visibility}
            {row.visibility_locked && (
              <span className="uni-field__hint">
                Locked by an administrator — the audience can't be changed here.
              </span>
            )}
          </UniDetailRow>
          {row.show_to_selected_internal_users && (
            <UniDetailRow label="Displayed for">
              {row.audience_user_details?.length
                ? row.audience_user_details
                    .map((u) => u.label || u.username).join(', ')
                : (
                  <span style={{ color: 'var(--ty-danger)' }}>
                    Nobody selected — this form will not appear on For Me.
                  </span>
                )}
            </UniDetailRow>
          )}
          <UniDetailRow label="Push reminder">
            {reminderAudienceLabel(row.reminder_push_audience)}
            {row.reminder_push_enabled
              ? ` · ${row.reminder_push_schedule_label
                || reminderScheduleLabel({
                  enabled: true,
                  time: row.reminder_push_time,
                  days: row.reminder_push_days,
                })}`
              : ''}
          </UniDetailRow>
          <UniDetailRow label="Accepting responses">
            <UniBool value={row.is_accepting} />
          </UniDetailRow>
          <UniDetailRow label={seesEverySubmission(row) ? 'Responses' : 'My submissions'}>
            {fmtCount(row.response_count)}
          </UniDetailRow>
          <UniDetailRow label="Questions">
            {fmtCount(row.question_count)}
          </UniDetailRow>
          <UniDetailRow label="Collects email">
            <UniBool value={row.collect_email} />
          </UniDetailRow>
          <UniDetailRow label="Respondents may edit submissions">
            <UniBool value={row.allow_edit_response} />
          </UniDetailRow>
          <UniDetailRow label="Admins may edit submissions">
            <UniBool value={row.allow_admin_edit_response} />
          </UniDetailRow>
          <UniDetailRow label="Requires sign-in">
            <UniBool value={row.requires_login || row.effective_requires_login} />
          </UniDetailRow>
          <UniDetailRow label="Owner">{row.created_by_name}</UniDetailRow>
          <UniDetailRow label="Created">
            {row.created_at ? new Date(row.created_at).toLocaleString() : ''}
          </UniDetailRow>
          <UniDetailRow label="Last updated">
            {row.updated_at ? new Date(row.updated_at).toLocaleString() : ''}
          </UniDetailRow>
        </>
      )}
    </UniModal>
  );
}


/** Secondary control under the copy-able URL — opens the confirm dialog. */
function FormLinkRefreshAction({ onAsk, busy }) {
  return (
    <div className="uni-copy-refresh">
      <button
        type="button"
        className="ty-btn ty-btn--sm ty-btn--secondary"
        onClick={onAsk}
        disabled={busy}
        title="Replace this URL — the current link will stop working"
      >
        {busy ? 'Creating…' : '↻ New link'}
      </button>
      <span className="uni-field__hint">
        Creates a new URL. Anyone still using the current link will no
        longer reach this form.
      </span>
    </div>
  );
}


function FormGsheetsModal({ form, onClose }) {
  const [draft, setDraft] = React.useState({
    spreadsheet_id: '', sheet_name: 'Responses', service_account_email: '',
    linked: false, sheets_enabled: false, sheets_configured: false,
  });
  const action = useUniAction();

  React.useEffect(() => {
    if (!form?.id) return;
    action.run(async () => {
      const data = await getJSON(`${EP.FORMS}${form.id}/gsheets/`);
      setDraft({
        spreadsheet_id: data.spreadsheet_id || '',
        sheet_name: data.sheet_name || 'Responses',
        service_account_email: data.service_account_email || '',
        linked: !!data.linked,
        sheets_enabled: !!data.sheets_enabled,
        sheets_configured: !!data.sheets_configured,
      });
    }).catch(() => {});
  }, [form?.id]);

  const save = () => action.run(async () => {
    const data = await patchJSON(`${EP.FORMS}${form.id}/gsheets/`, {
      spreadsheet_id: draft.spreadsheet_id.trim(),
      sheet_name: draft.sheet_name.trim() || 'Responses',
    });
    setDraft((d) => ({ ...d, ...data, linked: true }));
  }).catch(() => {});

  const unlink = () => {
    if (!window.confirm('Stop syncing new responses to this sheet?')) return;
    action.run(async () => {
      await deleteJSON(`${EP.FORMS}${form.id}/gsheets/`);
      setDraft((d) => ({ ...d, linked: false }));
    }).catch(() => {});
  };

  return (
    <UniModal
      open={!!form}
      title={form ? `Google Sheet — ${form.title}` : 'Google Sheet'}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="ty-btn ty-btn--secondary" onClick={onClose}>
            Close
          </button>
          {draft.linked && (
            <button type="button" className="ty-btn ty-btn--secondary"
                    onClick={unlink} disabled={action.busy}>
              Unlink
            </button>
          )}
          <button type="button" className="ty-btn" onClick={save}
                  disabled={action.busy || !draft.spreadsheet_id.trim()}>
            {action.busy ? 'Saving…' : 'Save link'}
          </button>
        </>
      )}
    >
      <UniFormError error={action.error} />
      <p className="uni-card__sub">
        New complete submissions append a row. Share the spreadsheet with the
        service account as Editor. This is the raw-response sheet — not a report export.
      </p>
      {!draft.sheets_configured && (
        <p className="uni-card__sub">
          Configure credentials under Business → Google Sheets first.
        </p>
      )}
      {draft.service_account_email && (
        <UniCopyField label="Share the sheet with" value={draft.service_account_email} />
      )}
      <UniTextField
        label="Spreadsheet ID" required
        value={draft.spreadsheet_id}
        onChange={(v) => setDraft((d) => ({ ...d, spreadsheet_id: v }))}
        placeholder="From the Google Sheets URL"
      />
      <UniTextField
        label="Worksheet name"
        value={draft.sheet_name}
        onChange={(v) => setDraft((d) => ({ ...d, sheet_name: v }))}
      />
    </UniModal>
  );
}


/** Active list, or the Deleted tab (`GET /forms/deleted/`). */
function formsCollectionUrl(tab, q) {
  const base = tab === 'deleted' ? EP.DELETED : EP.FORMS;
  if (!q) return base;
  return `${base}?search=${encodeURIComponent(q)}`;
}


/** Confirm-then-POST restore. Same shape as ``useUniDelete`` so the modal fits. */
function useRestoreForm(onRestored) {
  const [row, setRow] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const ask = React.useCallback((record) => {
    setRow(record);
    setError(null);
  }, []);

  const cancel = React.useCallback(() => {
    if (busy) return;
    setRow(null);
    setError(null);
  }, [busy]);

  const confirm = React.useCallback(async () => {
    if (!row) return false;
    setBusy(true);
    setError(null);
    try {
      await postJSON(`${EP.FORMS}${row.id}/restore/`, {});
      const restored = row;
      setRow(null);
      onRestored?.(restored);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  }, [row, onRestored]);

  return { open: row !== null, row, busy, error, ask, cancel, confirm };
}


function FormsListTabs({ tab, onChange }) {
  const items = [
    { id: 'active', label: 'Forms' },
    { id: 'deleted', label: 'Deleted' },
  ];
  return (
    <div className="uni-list-tabs" role="tablist" aria-label="Form lists">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          id={`uni-forms-tab-${item.id}`}
          aria-selected={tab === item.id}
          aria-controls="uni-forms-tabpanel"
          className={`uni-list-tab${tab === item.id ? ' uni-list-tab--on' : ''}`}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}


export function UniFormsForms() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'deleted' ? 'deleted' : 'active';
  const deletedTab = tab === 'deleted';
  const [q, setQ] = React.useState('');
  const tz = useOrgTimeZone();
  const url = formsCollectionUrl(tab, q);
  const { rows, loading, error, raw, reload } = useUniList(url, [q, tab]);
  const [moved, setMoved] = React.useState(null);
  const [restoredNote, setRestoredNote] = React.useState(null);
  const [viewing, setViewing] = React.useState(null);
  const [sheetsForm, setSheetsForm] = React.useState(null);
  const navigate = useNavigate();

  const create = useUniCreate(
    EP.FORMS,
    { ...NEW_FORM_DRAFT },
    // A newly created form is empty, and an empty form collects nothing.
    // Rather than drop the user back on a list where the new row reads
    // "0 ⚠ questions", take them straight to the builder — adding the
    // questions *is* the rest of creating a form.
    (created) => {
      reload();
      if (created?.id) navigate(ROUTES.UNI_FORMS_FORM_QUESTIONS(created.id));
    },
  );
  const edit = useUniEdit(EP.FORMS, () => reload());
  const setTab = React.useCallback((next) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'deleted') nextParams.set('tab', 'deleted');
    else nextParams.delete('tab');
    setParams(nextParams, { replace: true });
  }, [params, setParams]);

  const del = useUniDelete(EP.FORMS, (row) => {
    setMoved(row);
    setRestoredNote(null);
    reload();
  });
  const restore = useRestoreForm((row) => {
    setRestoredNote(row);
    setMoved(null);
    reload();
  });
  // One hook for the whole table: ``isCopied(r.id)`` means only the row
  // that was clicked flashes "Copied", not every Copy button at once.
  const copy = useUniCopy();
  const dup  = useDuplicateForm(reload);
  const refreshLink = useRefreshFormLink((updated) => {
    reload();
    // Keep the View drawer in sync if it is open on this row — otherwise
    // the URL box would still show the retired link until the drawer
    // was closed and reopened.
    setViewing((v) => (v && updated && v.id === updated.id ? updated : v));
  });
  const reminder = useSendFormReminder(() => reload());

  const columns = [
    { key: 'title', label: 'Title',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.title}</span> },
    { key: 'form_type', label: 'Type',
      render: (r) => r.form_type ? <UniStatusPill value={r.form_type} palette="cyan" /> : '—' },
    { key: 'status', label: 'Status',
      render: (r) => <UniStatusPill value={r.status} /> },
    { key: 'response_count', label: 'Responses', align: 'right',
      render: (r) => <SubmissionCount row={r} /> },
    // A form with 0 questions can't be filled in, so this is the column
    // that tells you which of your forms is actually finished.
    { key: 'question_count', label: 'Questions', align: 'right',
      render: (r) => (
        r.question_count
          ? <span className="uni-num">{fmtCount(r.question_count)}</span>
          : (
            <span className="uni-num" style={{ color: 'var(--ty-danger)' }}
                  title="This form has no questions — there's nothing to fill in.">
              0 ⚠
            </span>
          )
      ) },
    { key: 'is_quiz',  label: 'Quiz?', align: 'center',
      render: (r) => <UniBool value={r.is_quiz} /> },
    // ---- Who it's for -----------------------------------------------
    // Public vs internal is fill access.  The For Me checkbox is a
    // second badge so a public form that is also listed for named
    // people is not mistaken for "internal users only".
    { key: 'visibility', label: 'Audience', align: 'center',
      render: (r) => {
        const n = r.audience_user_details?.length || 0;
        const forMe = r.show_to_selected_internal_users
          || (r.show_to_selected_internal_users !== false && r.visibility === 'restricted');
        const fill = r.visibility === 'restricted'
          ? <UniStatusPill value="restricted" palette="violet" />
          : r.visibility === 'internal'
            ? <UniStatusPill value="internal" palette="amber" />
            : <UniStatusPill value="public" palette="green" />;
        return (
          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
            {fill}
            {forMe && (
              <UniStatusPill
                value={n ? `${n} on For Me` : 'For Me: nobody'}
                palette={n ? 'violet' : 'red'}
              />
            )}
          </span>
        );
      } },
    { key: 'updated_at', label: 'Updated',
      render: (r) => r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—' },
    ...(deletedTab ? [{
      key: 'deleted_at', label: 'Deleted',
      render: (r) => (r.deleted_at ? formatInTimeZone(r.deleted_at, tz) : '—'),
    }] : []),
    // ---- The share link, in the list itself -------------------------
    // Two jobs in one narrow column: confirm the form HAS a live link
    // (an "Open ↗" you can click and see for yourself) and show its
    // slug so a user glancing down the table can tell two similarly
    // named forms apart.  Deliberately not the full URL: the host repeats
    // on every row, so it would be a column of identical prefixes and no
    // room left for the part that differs.  The full link lives in the
    // View drawer, and the Copy action next door takes the whole thing.
    { key: 'link', label: 'Link',
      render: (r) => {
        const link = formPublicUrl(r);
        if (!link) return <span className="uni-num--muted">—</span>;
        if (r.is_deleted) {
          return (
            <span className="uni-num--muted"
                  title="Restore this form to turn its public link back on">
              paused
            </span>
          );
        }
        if (!canShareForm(r)) {
          return (
            <span className="uni-num--muted"
                  title="Publish this form to activate its link">
              not live
            </span>
          );
        }
        return (
          <a href={link} target="_blank" rel="noreferrer"
             title={`Open the form as a respondent — ${link}`}>
            /{r.slug} ↗
          </a>
        );
      } },
    // The verbs.  ``can_*`` comes from the serializer, which asks the
    // same ``api_permissions`` predicates the write endpoints enforce —
    // so what's rendered and what's allowed can't drift apart.  An admin
    // gets every button; a colleague with read-only access gets View and
    // Responses only.
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => {
        const link = formPublicUrl(r);
        const shareable = canShareForm(r);
        const copied = copy.isCopied(r.id);
        return (
          <UniRowActions items={[
            // One-click share.  First verb in the row because "send this
            // form to someone" is the most frequent thing anyone does
            // with a finished form, and until now the only way to get the
            // URL was to open the View drawer and select it by hand.
            // Hidden (not disabled) on a draft: its link resolves to a
            // "not available" page, so offering Copy there would hand out
            // a URL that looks fine and doesn't work.
            { key: 'copy',
              label: copied ? '✓ Copied' : '🔗 Copy link',
              title: shareable
                ? `Copy the fill link — ${link}`
                : 'Publish this form to get a working link',
              show: shareable,
              done: copied,
              onClick: () => copy.copy(link, r.id) },
            // Sits next to Copy because it is the other thing you do
            // with a live URL: retire it.  Confirm is in a modal, not
            // on this click — rotating is irreversible for anyone who
            // already has the current link.
            { key: 'refresh-link',
              label: '↻ New link',
              title: canRefreshFormLink(r)
                ? 'Replace this form\'s public URL — the current link will stop working'
                : 'You cannot change this form\'s link',
              show: shareable && canRefreshFormLink(r),
              busy: refreshLink.busy && refreshLink.row?.id === r.id,
              onClick: () => refreshLink.ask(r) },
            { key: 'remind',
              label: '🔔 Remind',
              title: canSendFormReminder(r)
                ? `Send a push reminder — ${reminderAudienceLabel(r.reminder_push_audience)}`
                : 'Publish this form to send a reminder',
              show: canSendFormReminder(r),
              busy: reminder.busy && reminder.row?.id === r.id,
              onClick: () => reminder.ask(r) },
            { key: 'view', label: 'View', title: 'See this form\'s settings and full link',
              onClick: () => setViewing(r) },
            // The builder.  A form with no questions collects nothing —
            // this is the next thing to do after creating one, not an
            // afterthought.
            { key: 'questions',
              label: r.question_count
                ? `Questions (${r.question_count})`
                : '+ Add questions',
              title: r.question_count
                ? 'Add, edit or reorder what this form asks'
                : 'This form has no questions yet — nothing to fill in',
              show: !deletedTab,
              to: ROUTES.UNI_FORMS_FORM_QUESTIONS(r.id) },
            { key: 'edit', label: 'Edit', title: 'Rename, publish or change who this form is for',
              show: !deletedTab && r.can_edit !== false,
              onClick: () => edit.start(r, EDIT_FORM_FIELDS) },
            // Sits next to Edit because it answers the same question —
            // "I need a different version of this" — and is the safe answer
            // once the form has responses: editing a live form re-words the
            // questions its existing submissions were answers to.
            //
            // Gated on ``can_duplicate`` rather than ``can_edit``: the copy
            // is a new form owned by the person clicking, so a read-only
            // collaborator may take one (the server applies the identical
            // predicate — see ``FormViewSet.duplicate``).
            { key: 'duplicate', label: 'Duplicate',
              title: r.response_count
                ? 'Copy this form and its questions into a new draft — the responses stay here'
                : 'Copy this form and its questions into a new draft',
              show: !deletedTab && r.can_duplicate !== false,
              busy: dup.busyId === r.id,
              onClick: () => dup.run(r) },
            { key: 'responses', label: 'Responses',
              title: `${fmtCount(r.response_count)} submission(s)`,
              show: r.can_view_responses !== false,
              to: ROUTES.UNI_FORMS_FORM_RESPONSES(r.id) },
            { key: 'my-responses', label: 'My responses',
              title: 'View only your submissions to this form',
              show: r.can_view_own_responses === true,
              to: formMyResponsesPath(r.id) },
            { key: 'gsheets', label: 'Sheets',
              title: 'Live Google Sheet for new responses',
              show: !deletedTab && r.can_edit !== false,
              onClick: () => setSheetsForm(r) },
            { key: 'restore', label: 'Restore',
              title: 'Put this form back on the Forms tab',
              show: deletedTab && r.can_delete !== false,
              onClick: () => restore.ask(r) },
            { key: 'delete', label: 'Delete', danger: true,
              title: 'Move this form to Deleted. You can restore it later.',
              show: !deletedTab && r.can_delete !== false,
              onClick: () => del.ask(r) },
          ]} />
        );
      } },
  ];
  return (
    <UniPageShell
      theme={THEME}
      icon="📝"
      title="Forms"
      subtitle={raw?.count != null
        ? (deletedTab
          ? `${fmtCount(raw.count)} deleted form${raw.count === 1 ? '' : 's'}`
          : `${fmtCount(raw.count)} form${raw.count === 1 ? '' : 's'}`)
        : (deletedTab ? 'Forms you can restore' : 'All custom forms')}
      actions={
        <>
          <UniActionButton hero primary onClick={create.start}>+ New Form</UniActionButton>
          <UniActionButton hero to="/uni-forms">← Dashboard</UniActionButton>
        </>
      }
    >
      <FormsListTabs tab={tab} onChange={setTab} />
      <div id="uni-forms-tabpanel" role="tabpanel" aria-labelledby={`uni-forms-tab-${tab}`}>
      {deletedTab && (
        <div className="uni-note uni-note--info" style={{ marginBottom: 12 }}>
          <span aria-hidden="true">ℹ️</span>
          <span>
            These forms are hidden from the Forms tab and from anyone trying
            to fill them. Restore a form to put it back, with its questions
            and responses.
          </span>
        </div>
      )}
      {moved && !deletedTab && (
        <div className="uni-state" style={{ marginBottom: 12 }}>
          <div className="uni-state__icon">🗂️</div>
          <div className="uni-state__title">
            “{moved.title}” was moved to Deleted.
          </div>
          <div className="uni-state__hint">
            The form and its responses are still saved. The public link stays
            off until you restore it.
          </div>
          <div className="uni-state__hint">
            <UniActionButton onClick={() => { setMoved(null); setTab('deleted'); }}>
              Open Deleted
            </UniActionButton>
            {' '}
            <UniActionButton onClick={() => setMoved(null)}>Dismiss</UniActionButton>
          </div>
        </div>
      )}
      {restoredNote && (
        <div className="uni-state" style={{ marginBottom: 12 }}>
          <div className="uni-state__icon">✅</div>
          <div className="uni-state__title">
            “{restoredNote.title}” is back on Forms.
          </div>
          <div className="uni-state__hint">
            It kept the status it had when you deleted it, and its responses
            are still there.
          </div>
          <div className="uni-state__hint">
            <UniActionButton onClick={() => { setRestoredNote(null); setTab('active'); }}>
              Open Forms
            </UniActionButton>
            {' '}
            <UniActionButton onClick={() => setRestoredNote(null)}>Dismiss</UniActionButton>
          </div>
        </div>
      )}
      <UniToolbar onSearch={setQ} placeholder={deletedTab ? 'Search deleted forms…' : 'Search forms…'}>
        {!deletedTab && (
          <UniActionButton primary onClick={create.start}>+ New Form</UniActionButton>
        )}
      </UniToolbar>
      {/* A failed Duplicate (a 403 from the row guard, a dropped
          connection) has no other visible result — the list simply
          reloads unchanged — so it has to be said out loud. */}
      <UniFormError error={dup.error} />
      <DuplicatedFormNote state={dup} />
      <SentReminderNote state={reminder} />
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty={deletedTab ? 'No deleted forms' : 'No forms yet'}
        emptyHint={deletedTab
          ? 'When you delete a form it shows up here, and you can restore it.'
          : 'Use “+ New Form” above to create your first form and start collecting submissions.'}
      />
      </div>
      <CreateFormModal create={create} />
      <EditFormModal edit={edit} />
      <ViewFormModal
        row={viewing}
        onClose={() => { if (!refreshLink.open) setViewing(null); }}
        onAskRefresh={refreshLink.ask}
        refreshBusy={refreshLink.busy}
      />
      <FormGsheetsModal form={sheetsForm} onClose={() => setSheetsForm(null)} />
      <UniConfirmModal
        state={refreshLink}
        title="Create a new link"
        confirmLabel="Create new link"
        busyLabel="Creating…"
      >
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Replace the public URL for <strong>{refreshLink.row?.title}</strong>?
          Anyone who still has the current link will no longer be able to
          open the form. Existing responses stay on this form.
        </p>
        <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
          <span aria-hidden="true">💡</span>
          <span>
            This cannot be undone. Copy the new link from the form afterwards
            and send that instead.
          </span>
        </div>
      </UniConfirmModal>
      <UniConfirmModal
        state={reminder}
        title="Send push reminder"
        confirmLabel="Send reminder"
        busyLabel="Sending…"
        danger={false}
      >
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Send a push notification for <strong>{reminder.row?.title}</strong> to{' '}
          <strong>
            {reminderAudienceLabel(reminder.row?.reminder_push_audience).toLowerCase()}
          </strong>
          ?
        </p>
        <div className="uni-note uni-note--info" style={{ marginTop: 12 }}>
          <span aria-hidden="true">ℹ️</span>
          <span>
            This goes to people this form is displayed for under For Me.
            Anyone without the mobile app installed will not see it until they
            sign in on a device.
          </span>
        </div>
      </UniConfirmModal>
      <UniConfirmModal
        state={del}
        title="Move to Deleted"
        confirmLabel="Move to Deleted"
        busyLabel="Moving…"
      >
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Move <strong>{del.row?.title}</strong> to Deleted?
          {del.row?.response_count > 0 && (
            <>
              {' '}Its{' '}
              <strong>{fmtCount(del.row.response_count)}</strong> recorded
              response{del.row.response_count === 1 ? '' : 's'} stay saved
              with it.
            </>
          )}
          {' '}You can restore it later from the Deleted tab. The public
          link stops working until you do.
        </p>
        <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
          <span aria-hidden="true">💡</span>
          <span>
            If you only want to stop new submissions, set the form&apos;s
            status to <strong>Closed</strong> instead — it stays on the
            Forms tab.
          </span>
        </div>
      </UniConfirmModal>
      <UniConfirmModal
        state={restore}
        title="Restore form"
        confirmLabel="Restore form"
        busyLabel="Restoring…"
        danger={false}
      >
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Restore <strong>{restore.row?.title}</strong>? It goes back to
          the Forms tab with the same status
          {restore.row?.status ? <> (<strong>{restore.row.status}</strong>)</> : null}
          {' '}and its responses. A published form&apos;s public link works again.
        </p>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   For Me — forms somebody else picked ME for
   ====================================================================
   The mirror image of the Forms list.  Every other screen in this module
   is scoped by ownership ("forms I built"); this one is scoped by
   AUDIENCE ("forms I was selected for"), which is a question the builder
   list structurally cannot answer — by definition these rows belong to
   somebody else.

   Backed by ``GET /api/uni-forms/forms/for-me/``, which resolves
   ``Form.selected_for(request.user)``: published forms with
   ``show_to_selected_internal_users`` where the caller is in
   ``audience_users`` or in one of the ``audience_groups``.  Fill access
   (public / internal / restricted) is a separate question.  The scoping
   is entirely server-side and takes no parameters, so there is nothing
   here a client could widen.

   Why the actions differ from the Forms list
   -----------------------------------------
   Being in a form's audience makes you a RESPONDENT, not a collaborator.
   So the primary verb is "Open & fill" — the same public link a colleague
   would paste into chat — and there is no Edit or Delete.  Those buttons
   are absent because ``can_edit`` / ``can_delete`` come back false for a
   recipient, and the endpoints enforce the same predicate; the row menu
   simply has nothing to draw.  "Responses" still appears when the server
   says the user may read them (an admin browsing this tab, or a form
   shared with them for that purpose).
   */

/** Page chrome for "For Me" — split out only to keep the page readable. */
function ForMeShell({ raw, rows, loading, error, columns, viewing, onCloseView }) {
  return (
    <UniPageShell
      theme={THEME}
      icon="🙋"
      title="For Me"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} form${raw.count === 1 ? '' : 's'} shared with you`
        : 'Forms you have been selected to fill'}
      actions={
        <>
          <UniActionButton hero to={ROUTES.UNI_FORMS_FORMS}>📝 My Forms</UniActionButton>
          <UniActionButton hero to={ROUTES.UNI_FORMS_HOME}>← Dashboard</UniActionButton>
        </>
      }
    >
      {/* Says out loud what put a row here.  Without it, "For Me" reads as
          a generic inbox and the empty state is a dead end — a user with
          nothing assigned has no way to tell that from a broken screen. */}
      <div className="uni-note uni-note--info">
        <span aria-hidden="true">ℹ️</span>
        <span>
          These are forms where somebody chose{' '}
          <strong>“Display this Form for selected Internal Users”</strong> and
          included you. Only published forms appear here.
        </span>
      </div>

      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="Nothing for you right now"
        emptyHint="When a colleague builds a form and selects you as one of its internal users, it shows up here."
      />
      <ViewFormModal row={viewing} onClose={onCloseView} />
    </UniPageShell>
  );
}


export function UniFormsForMe() {
  const { rows, loading, error, raw } = useUniList(EP.FOR_ME);
  const [viewing, setViewing] = React.useState(null);
  const copy = useUniCopy();

  const columns = [
    { key: 'title', label: 'Form',
      render: (r) => (
        <span style={{ fontWeight: 600 }}>{r.title}</span>
      ) },
    // Who is asking.  The single most useful column on this screen: an
    // unexplained form in your list is easy to ignore, one from your
    // manager is not.
    { key: 'created_by_name', label: 'From',
      render: (r) => r.created_by_name || '—' },
    { key: 'description', label: 'About',
      render: (r) => (
        r.description
          ? <span title={r.description}>
              {r.description.length > 70
                ? `${r.description.slice(0, 70)}…`
                : r.description}
            </span>
          : <span className="uni-num--muted">—</span>
      ) },
    { key: 'question_count', label: 'Questions', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.question_count)}</span> },
    { key: 'response_count', label: 'Submissions', align: 'right',
      render: (r) => <SubmissionCount row={r} /> },
    // ``is_accepting`` rather than ``status``: a published form that hit
    // its response cap or its end date is "published" but cannot be
    // filled, and on this screen the only thing the reader cares about is
    // whether clicking through will work.
    { key: 'is_accepting', label: 'Open?', align: 'center',
      render: (r) => (
        r.is_accepting
          ? <UniStatusPill value="accepting" palette="green" />
          : <UniStatusPill value="closed" palette="gray" />
      ) },
    { key: 'end_date', label: 'Closes',
      render: (r) => (
        r.end_date
          ? new Date(r.end_date).toLocaleDateString()
          : <span className="uni-num--muted">no deadline</span>
      ) },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => {
        const link = formPublicUrl(r);
        const copied = copy.isCopied(r.id);
        return (
          <UniRowActions items={[
            // ``to`` would push a SPA route; this is an ordinary link out
            // to the public fill page, which is mounted OUTSIDE the
            // authenticated shell.  ``show`` gates it on the form actually
            // being open, so nobody is sent to a "form closed" card.
            { key: 'fill', label: 'Open & fill ↗',
              title: r.is_accepting
                ? `Fill in this form — ${link}`
                : 'This form is not accepting responses right now',
              show: !!link && r.is_accepting,
              href: link,
              // New tab so a half-typed answer isn't lost if the user
              // comes back to this list mid-way through.
              newTab: true },
            { key: 'copy',
              label: copied ? '✓ Copied' : '🔗 Copy link',
              title: 'Copy the link — e.g. to finish this on your phone',
              show: !!link,
              done: copied,
              onClick: () => copy.copy(link, r.id) },
            { key: 'view', label: 'Details',
              title: 'See what this form is and who it is for',
              onClick: () => setViewing(r) },
            // Only when the server says so — see the note above.
            { key: 'responses', label: 'Responses',
              title: seesEverySubmission(r)
                ? `${fmtCount(r.response_count)} submission(s)`
                : `${fmtCount(r.response_count)} of your submission(s)`,
              show: r.can_view_responses === true,
              to: ROUTES.UNI_FORMS_FORM_RESPONSES(r.id) },
            { key: 'my-responses', label: 'My responses',
              title: 'View only your submissions to this form',
              show: r.can_view_own_responses === true,
              to: formMyResponsesPath(r.id) },
          ]} />
        );
      } },
  ];

  return <ForMeShell raw={raw} rows={rows} loading={loading} error={error}
                     columns={columns} viewing={viewing}
                     onCloseView={() => setViewing(null)} />;
}


/* ====================================================================
   My Submissions — forms I have actually filled
   ====================================================================
   For Me is a mailbox of forms somebody picked you for.  This screen
   is the filled-forms inbox: every form you submitted a complete
   response to, including public links that never appeared on For Me.

   Backed by ``GET /api/uni-forms/forms/my-submissions/``, which
   resolves ``Form.submitted_by(request.user)``.  Always self-scoped
   — there is no ``?user=`` parameter.  Opening a row uses
   ``?self=1`` on the nested responses endpoint so even a form owner
   who also filled the form only sees their own answers here.
   */

function fmtSubmittedAt(value, timeZone) {
  if (!value) return '—';
  return formatInTimeZone(value, timeZone);
}


export function UniFormsMySubmissions() {
  const tz = useOrgTimeZone();
  const { rows, loading, error, raw } = useUniList(EP.MY_SUBMISSIONS);

  const columns = [
    { key: 'title', label: 'Form',
      render: (r) => (
        <span style={{ fontWeight: 600 }}>{r.title}</span>
      ) },
    { key: 'created_by_name', label: 'From',
      render: (r) => r.created_by_name || '—' },
    { key: 'own_response_count', label: 'My submissions', align: 'right',
      render: (r) => (
        <span className="uni-num">{fmtCount(r.own_response_count)}</span>
      ) },
    { key: 'last_submitted_at', label: 'Last submitted',
      render: (r) => fmtSubmittedAt(r.last_submitted_at, tz) },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'my-responses', label: 'View my responses',
            title: 'Only the answers you submitted',
            to: formMyResponsesPath(r.id) },
          { key: 'edit', label: 'Edit my response ↗',
            title: r.is_accepting
              ? 'Change the answers you already submitted'
              : 'This form is not accepting changes right now',
            show: !!r.allow_edit_response && !!formPublicEditUrl(r) && r.is_accepting,
            href: formPublicEditUrl(r),
            newTab: true },
          { key: 'fill', label: 'Open form ↗',
            title: r.is_accepting
              ? 'Fill in this form again'
              : 'This form is not accepting responses right now',
            show: !!formPublicUrl(r) && r.is_accepting,
            href: formPublicUrl(r),
            newTab: true },
        ]} />
      ) },
  ];

  return (
    <UniPageShell
      theme={THEME}
      icon="📥"
      title="My Submissions"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} form${raw.count === 1 ? '' : 's'} you have filled`
        : 'Forms you have submitted a response to'}
      actions={
        <>
          <UniActionButton hero to={ROUTES.UNI_FORMS_FOR_ME}>🙋 For Me</UniActionButton>
          <UniActionButton hero to={ROUTES.UNI_FORMS_HOME}>← Dashboard</UniActionButton>
        </>
      }
    >
      <div className="uni-note uni-note--info">
        <span aria-hidden="true">ℹ️</span>
        <span>
          These are forms you have submitted while signed in. You only
          ever see <strong>your own answers</strong> here — never
          anybody else's.
        </span>
      </div>
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="You have not submitted any forms yet"
        emptyHint="When you fill a form while signed in, it shows up here so you can re-read your answers."
      />
    </UniPageShell>
  );
}


/* ====================================================================
   Quizzes
   ==================================================================== */

export function UniFormsQuizzes() {
  const { rows, loading, error, raw, reload } = useUniList(EP.QUIZZES);

  const create = useUniCreate(
    EP.QUIZZES,
    {
      title: '', description: '', quiz_type: 'knowledge', status: 'draft',
      pass_percentage: '70', requires_login: false,
    },
    () => reload(),
  );
  const edit = useUniEdit(EP.QUIZZES, () => reload());
  const del  = useUniDelete(EP.QUIZZES, () => reload());
  const { draft } = create;
  const canSave = draft.title.trim().length > 0;

  const columns = [
    { key: 'title', label: 'Title',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.title}</span> },
    { key: 'quiz_type', label: 'Type',
      render: (r) => r.quiz_type ? <UniStatusPill value={r.quiz_type} palette="cyan" /> : '—' },
    { key: 'status', label: 'Status',
      render: (r) => <UniStatusPill value={r.status} /> },
    { key: 'response_count', label: 'Responses', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.response_count)}</span> },
    { key: 'total_points', label: 'Points', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.total_points)}</span> },
    { key: 'pass_percentage', label: 'Pass %', align: 'right',
      render: (r) => <span className="uni-num">{fmtPct(r.pass_percentage)}</span> },
    // "Attempts" rather than "Responses" — a quiz submission is a graded
    // attempt, and the page it opens is scored, not a flat answer dump.
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'edit', label: 'Edit',
            title: 'Rename, re-grade or publish this quiz',
            show: r.can_edit !== false,
            onClick: () => edit.start(r, [
              'title', 'description', 'quiz_type', 'status',
              'pass_percentage', 'requires_login',
            ]) },
          { key: 'attempts', label: 'Attempts',
            title: `${fmtCount(r.response_count)} attempt(s)`,
            show: r.can_view_responses !== false,
            to: ROUTES.UNI_FORMS_QUIZ_RESPONSES(r.id) },
          { key: 'delete', label: 'Delete', danger: true,
            title: 'Permanently delete this quiz and its attempts',
            show: r.can_delete !== false,
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];

  return (
    <UniPageShell
      theme={THEME}
      icon="🧠"
      title="Quizzes"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} quiz${raw.count === 1 ? '' : 'zes'}`
        : 'All scored quizzes'}
      actions={
        <>
          <UniActionButton hero primary onClick={create.start}>+ New Quiz</UniActionButton>
          <UniActionButton hero to="/uni-forms">← Dashboard</UniActionButton>
        </>
      }
    >
      <UniToolbar>
        <UniActionButton primary onClick={create.start}>+ New Quiz</UniActionButton>
      </UniToolbar>
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No quizzes yet"
        emptyHint="Use “+ New Quiz” above to grade respondents and collect insights."
      />

      <UniModal
        open={create.open}
        title="New quiz"
        onClose={create.close}
        footer={
          <CreateFooter
            busy={create.busy}
            onCancel={create.close}
            disabled={!canSave}
            saveLabel="Create quiz"
            onSave={() => create.submit({
              ...draft,
              title: draft.title.trim(),
              // Knowledge quizzes are scored, so the pass-mark matters;
              // personality quizzes are outcome-based and ignore it.
              pass_percentage: Number(draft.pass_percentage) || 0,
            })}
          />
        }
      >
        <UniFormError error={create.error} />
        <UniTextField
          label="Title"
          required
          autoFocus
          value={draft.title}
          onChange={(v) => create.set('title', v)}
          placeholder="e.g. Product knowledge check"
        />
        <UniTextArea
          label="Description"
          value={draft.description}
          onChange={(v) => create.set('description', v)}
          placeholder="Shown on the quiz intro screen."
        />
        <div className="uni-form-row">
          <UniSelectField
            label="Quiz type"
            value={draft.quiz_type}
            onChange={(v) => create.set('quiz_type', v)}
            options={QUIZ_TYPE_OPTIONS}
          />
          <UniSelectField
            label="Status"
            value={draft.status}
            onChange={(v) => create.set('status', v)}
            options={STATUS_OPTIONS}
            hint="Drafts are private until you publish."
          />
        </div>
        {draft.quiz_type === 'knowledge' && (
          <UniTextField
            label="Pass percentage"
            value={draft.pass_percentage}
            onChange={(v) => create.set('pass_percentage', v.replace(/[^0-9]/g, ''))}
            placeholder="70"
            hint="Score needed to pass, as a whole number (0–100)."
          />
        )}
        <UniCheckField
          label="Require sign-in to take this quiz"
          checked={!!draft.requires_login}
          onChange={(v) => create.set('requires_login', v)}
        />
      </UniModal>

      {/* Edit — same shape as the create modal.  The pass-mark is shown
          unconditionally here (unlike on create) because an author may
          be converting a personality quiz to a knowledge one, and
          hiding the field would silently keep a stale 0. */}
      <UniModal
        open={edit.open}
        title="Edit quiz"
        onClose={edit.close}
        footer={
          <>
            <button type="button" className="ty-btn ty-btn--secondary"
                    onClick={edit.close} disabled={edit.busy}>
              Cancel
            </button>
            <button type="button" className="ty-btn"
                    onClick={() => edit.submit({
                      ...edit.draft,
                      title: String(edit.draft.title || '').trim(),
                      pass_percentage: Number(edit.draft.pass_percentage) || 0,
                    })}
                    disabled={edit.busy || !String(edit.draft.title || '').trim()}>
              {edit.busy ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        <UniFormError error={edit.error} />
        <UniTextField
          label="Title" required autoFocus
          value={edit.draft.title || ''}
          onChange={(v) => edit.set('title', v)}
        />
        <UniTextArea
          label="Description"
          value={edit.draft.description || ''}
          onChange={(v) => edit.set('description', v)}
        />
        <div className="uni-form-row">
          <UniSelectField
            label="Quiz type"
            value={edit.draft.quiz_type || 'knowledge'}
            onChange={(v) => edit.set('quiz_type', v)}
            options={QUIZ_TYPE_OPTIONS}
          />
          <UniSelectField
            label="Status"
            value={edit.draft.status || 'draft'}
            onChange={(v) => edit.set('status', v)}
            options={STATUS_OPTIONS}
          />
        </div>
        <UniTextField
          label="Pass percentage"
          value={String(edit.draft.pass_percentage ?? '')}
          onChange={(v) => edit.set('pass_percentage', v.replace(/[^0-9]/g, ''))}
          hint="Score needed to pass, as a whole number (0–100). Ignored by personality quizzes."
        />
        <UniCheckField
          label="Require sign-in to take this quiz"
          checked={!!edit.draft.requires_login}
          onChange={(v) => edit.set('requires_login', v)}
        />
      </UniModal>

      <UniConfirmModal state={del} title="Delete quiz" confirmLabel="Delete quiz">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete <strong>{del.row?.title}</strong>?
          {del.row?.response_count > 0 && (
            <>
              {' '}Its <strong>{fmtCount(del.row.response_count)}</strong> recorded
              attempt{del.row.response_count === 1 ? '' : 's'} — and the scores
              on them — go too.
            </>
          )}
          {' '}This can't be undone.
        </p>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   Polls
   ==================================================================== */

export function UniFormsPolls() {
  const { rows, loading, error, raw, reload } = useUniList(EP.POLLS);

  const create = useUniCreate(
    EP.POLLS,
    {
      title: '', question: '', status: 'draft', result_display: 'after_vote',
      allow_multiple: false, allow_other: false,
    },
    () => reload(),
  );
  const edit = useUniEdit(EP.POLLS, () => reload());
  const del  = useUniDelete(EP.POLLS, () => reload());
  const { draft } = create;
  // ``question`` is the only field the Poll model requires beyond the
  // title (no default on the model), so gate Save on both.
  const canSave = draft.title.trim().length > 0 && draft.question.trim().length > 0;

  const columns = [
    { key: 'title',    label: 'Title',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.title}</span> },
    { key: 'question', label: 'Question',
      render: (r) => <span className="uni-num--muted">{r.question || '—'}</span> },
    { key: 'status',   label: 'Status',
      render: (r) => <UniStatusPill value={r.status} /> },
    { key: 'vote_count', label: 'Votes', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.vote_count)}</span> },
    { key: 'allow_multiple', label: 'Multi?', align: 'center',
      render: (r) => <UniBool value={r.allow_multiple} /> },
    // "Results" is a poll's equivalent of "Responses" — nobody wants to
    // read a poll one vote at a time, they want the tally.
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'edit', label: 'Edit',
            title: 'Reword the question, publish or close this poll',
            show: r.can_edit !== false,
            onClick: () => edit.start(r, [
              'title', 'question', 'status', 'result_display',
              'allow_multiple', 'allow_other',
            ]) },
          { key: 'results', label: 'Results',
            title: `${fmtCount(r.vote_count)} vote(s)`,
            show: r.can_view_responses !== false,
            to: ROUTES.UNI_FORMS_POLL_RESULTS(r.id) },
          { key: 'delete', label: 'Delete', danger: true,
            title: 'Permanently delete this poll and its votes',
            show: r.can_delete !== false,
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];

  return (
    <UniPageShell
      theme={THEME}
      icon="📊"
      title="Polls"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} poll${raw.count === 1 ? '' : 's'}`
        : 'All polls'}
      actions={
        <>
          <UniActionButton hero primary onClick={create.start}>+ New Poll</UniActionButton>
          <UniActionButton hero to="/uni-forms">← Dashboard</UniActionButton>
        </>
      }
    >
      <UniToolbar>
        <UniActionButton primary onClick={create.start}>+ New Poll</UniActionButton>
      </UniToolbar>
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No polls yet"
        emptyHint="Use “+ New Poll” above to create a quick single-question poll for instant feedback."
      />

      <UniModal
        open={create.open}
        title="New poll"
        onClose={create.close}
        footer={
          <CreateFooter
            busy={create.busy}
            onCancel={create.close}
            disabled={!canSave}
            saveLabel="Create poll"
            onSave={() => create.submit({
              ...draft,
              title: draft.title.trim(),
              question: draft.question.trim(),
            })}
          />
        }
      >
        <UniFormError error={create.error} />
        <UniTextField
          label="Title"
          required
          autoFocus
          value={draft.title}
          onChange={(v) => create.set('title', v)}
          placeholder="Internal name, e.g. Office lunch poll"
        />
        <UniTextField
          label="Question"
          required
          value={draft.question}
          onChange={(v) => create.set('question', v)}
          placeholder="What voters are asked, e.g. Where should we order from?"
        />
        <div className="uni-form-row">
          <UniSelectField
            label="Status"
            value={draft.status}
            onChange={(v) => create.set('status', v)}
            options={STATUS_OPTIONS}
            hint="Drafts are private until you publish."
          />
          <UniSelectField
            label="Show results"
            value={draft.result_display}
            onChange={(v) => create.set('result_display', v)}
            options={POLL_RESULT_OPTIONS}
          />
        </div>
        <UniCheckField
          label="Allow more than one choice"
          checked={!!draft.allow_multiple}
          onChange={(v) => create.set('allow_multiple', v)}
        />
        <UniCheckField
          label="Add an “Other” free-text option"
          checked={!!draft.allow_other}
          onChange={(v) => create.set('allow_other', v)}
        />
      </UniModal>

      <UniModal
        open={edit.open}
        title="Edit poll"
        onClose={edit.close}
        footer={
          <>
            <button type="button" className="ty-btn ty-btn--secondary"
                    onClick={edit.close} disabled={edit.busy}>
              Cancel
            </button>
            <button type="button" className="ty-btn"
                    onClick={() => edit.submit({
                      ...edit.draft,
                      title: String(edit.draft.title || '').trim(),
                      question: String(edit.draft.question || '').trim(),
                    })}
                    disabled={edit.busy
                      || !String(edit.draft.title || '').trim()
                      || !String(edit.draft.question || '').trim()}>
              {edit.busy ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        <UniFormError error={edit.error} />
        <UniTextField
          label="Title" required autoFocus
          value={edit.draft.title || ''}
          onChange={(v) => edit.set('title', v)}
        />
        <UniTextField
          label="Question" required
          value={edit.draft.question || ''}
          onChange={(v) => edit.set('question', v)}
        />
        {/* Editing the wording after voting has started doesn't reset the
            tally, which is usually what you want (fixing a typo) but
            occasionally isn't (changing the meaning).  Say so rather
            than blocking the edit. */}
        {edit.row?.vote_count > 0 && (
          <div className="uni-note uni-note--warn">
            <span aria-hidden="true">⚠️</span>
            <span>
              This poll already has {fmtCount(edit.row.vote_count)} vote(s).
              Rewording the question won't reset them — existing votes stay
              attached to the options they were cast for.
            </span>
          </div>
        )}
        <div className="uni-form-row">
          <UniSelectField
            label="Status"
            value={edit.draft.status || 'draft'}
            onChange={(v) => edit.set('status', v)}
            options={STATUS_OPTIONS}
          />
          <UniSelectField
            label="Show results"
            value={edit.draft.result_display || 'after_vote'}
            onChange={(v) => edit.set('result_display', v)}
            options={POLL_RESULT_OPTIONS}
          />
        </div>
        <UniCheckField
          label="Allow more than one choice"
          checked={!!edit.draft.allow_multiple}
          onChange={(v) => edit.set('allow_multiple', v)}
        />
        <UniCheckField
          label="Add an “Other” free-text option"
          checked={!!edit.draft.allow_other}
          onChange={(v) => edit.set('allow_other', v)}
        />
      </UniModal>

      <UniConfirmModal state={del} title="Delete poll" confirmLabel="Delete poll">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete <strong>{del.row?.title}</strong>?
          {del.row?.vote_count > 0 && (
            <>
              {' '}Its <strong>{fmtCount(del.row.vote_count)}</strong>{' '}
              vote{del.row.vote_count === 1 ? '' : 's'} will be lost — export
              the results first if you need them.
            </>
          )}
          {' '}This can't be undone.
        </p>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   Templates
   ==================================================================== */

/* The template gallery is served by a ``ReadOnlyModelViewSet``
   (``FormTemplateViewSet``) — these are curated blueprints shipped with
   the product, not user content, so there is deliberately no PATCH or
   DELETE route.  Offering Edit/Delete here would just 405.  View is
   genuinely useful though: it shows what you'll get before you clone. */
const TEMPLATE_FIELDS = [
  { key: 'name',            label: 'Template name', editable: false },
  { key: 'description',     label: 'Description',   editable: false },
  { key: 'category',        label: 'Category',      editable: false },
  { key: 'thumbnail_emoji', label: 'Icon',          editable: false },
  { key: 'question_count',  label: 'Questions',     editable: false,
    render: (r) => (r.blueprint?.questions || []).length || '—' },
];

export function UniFormsTemplates() {
  const { rows, loading, error, raw } = useUniList(EP.TEMPLATES);
  const [viewing, setViewing] = React.useState(null);


  // ``useTemplate`` POSTs to the ``use`` action on the template detail
  // route, which clones the blueprint into a fresh draft Form (see
  // ``FormTemplateViewSet.use``).  Kept as local state rather than
  // ``useUniCreate`` because there's no draft to collect — one click
  // is the whole interaction.
  const [busyId, setBusyId] = React.useState(null);
  const [useError, setUseError] = React.useState(null);
  const [created, setCreated] = React.useState(null);

  const useTemplate = async (row) => {
    setBusyId(row.id);
    setUseError(null);
    setCreated(null);
    try {
      // Empty body on purpose — the blueprint lives server-side; pass
      // ``{}`` rather than nothing so axios still sends a JSON content
      // type and DRF parses it cleanly.
      const form = await postJSON(`${EP.TEMPLATES}${row.id}/use/`, {});
      setCreated(form);
    } catch (err) {
      setUseError(err);
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    { key: 'thumbnail_emoji', label: '', align: 'center',
      render: (r) => (
        <span style={{ fontSize: 22, display: 'inline-block', lineHeight: 1 }}>
          {r.thumbnail_emoji || '🎨'}
        </span>
      ) },
    { key: 'name', label: 'Name',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'description', label: 'Description',
      render: (r) => <span className="uni-num--muted">{r.description || '—'}</span> },
    { key: 'category', label: 'Category',
      render: (r) => r.category ? <UniStatusPill value={r.category} palette="violet" /> : '—' },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'view', label: 'View',
            title: 'Preview what this template contains',
            onClick: () => setViewing(r) },
          { key: 'use',
            label: busyId === r.id ? 'Creating…' : '+ Use template',
            title: 'Create a new draft form from this template',
            disabled: busyId === r.id,
            onClick: () => useTemplate(r) },
        ]} />
      ) },
  ];

  return (
    <UniPageShell
      theme={THEME}
      icon="🎨"
      title="Form Templates"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} template${raw.count === 1 ? '' : 's'}`
        : 'Pre-built form templates gallery'}
      actions={
        <>
          <UniActionButton hero primary to="/uni-forms/forms">+ New Form</UniActionButton>
          <UniActionButton hero to="/uni-forms">← Dashboard</UniActionButton>
        </>
      }
    >
      {useError && (
        <div className="ty-error" style={{ marginBottom: 12 }}>
          <div className="ty-error__head">
            <span className="ty-error__icon">⚠️</span>
            Couldn't create a form from that template.
          </div>
          <div className="ty-error__trail">{String(useError.message || useError)}</div>
        </div>
      )}
      {created && (
        <div className="uni-state" style={{ marginBottom: 12 }}>
          <div className="uni-state__icon">✅</div>
          <div className="uni-state__title">
            Draft form “{created.title}” created from the template.
          </div>
          <div className="uni-state__hint">
            <UniActionButton to="/uni-forms/forms">Open Forms list →</UniActionButton>
          </div>
        </div>
      )}
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No templates available"
        emptyHint="Templates make it quick to spin up a survey or quiz — you can also start from a blank form."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="Template details" fields={TEMPLATE_FIELDS} />
    </UniPageShell>
  );
}

